import type { Promotion } from '@ahm/api-wrappers-core';
import { Box, ScrollArea, Space, Stack } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import EmptyPromotion from './empty-promos';
import PromoItemLoading from './promo-item-loading';
import VoucherTicket from './voucher/voucher-ticket';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

type ListPromosProps = {
  promotionsData?: {
    data?: any;
    isLoading?: boolean;
    fetchNextPage?: () => Promise<any>;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
  };
  selectedVoucherIds: string[];
  toggleVoucher: (voucher: Promotion, allVouchers: Promotion[]) => void;
};

export default function ListPromos({
  promotionsData,
  selectedVoucherIds,
  toggleVoucher,
}: ListPromosProps) {
  const isMobile = useIsMobile();
  const viewport = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const [scrollPosition, onScrollPositionChange] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleScroll = async () => {
      if (
        !viewport.current ||
        isLoadingRef.current ||
        !promotionsData?.fetchNextPage ||
        !promotionsData?.hasNextPage
      )
        return;

      const { scrollHeight, clientHeight, scrollTop } = viewport.current;
      const scrollThreshold = 10;

      const isNearBottom =
        scrollHeight - clientHeight - scrollTop <= scrollThreshold;

      if (isNearBottom && promotionsData.hasNextPage && !isLoadingRef.current) {
        try {
          isLoadingRef.current = true;
          await promotionsData.fetchNextPage();
        } finally {
          isLoadingRef.current = false;
        }
      }
    };
    const currentViewport = viewport.current;
    currentViewport?.addEventListener('scroll', handleScroll);
    return () => currentViewport?.removeEventListener('scroll', handleScroll);
  }, [viewport, scrollPosition, promotionsData]);

  return (
    <Box w="100%">
      <ScrollArea
        viewportRef={viewport}
        onScrollPositionChange={onScrollPositionChange}
        styles={{
          root: {
            overflowX: 'hidden',
            height: isMobile ? 'calc(100svh - 150px)' : 400,
          },
        }}
      >
        {promotionsData?.isLoading ? (
          <PromoItemLoading justify="center" align="center" />
        ) : promotionsData?.data?.pages?.some((page) => page.length > 0) ? (
          <>
            {promotionsData.data.pages.map((promo, index) => (
              <Stack key={index} justify="center" align="center" pb={16}>
                {promo.map((pro) => (
                  <VoucherTicket
                    key={pro._id}
                    voucher={pro}
                    listVoucher={promotionsData.data.pages.flat()}
                    selectedVoucherIds={selectedVoucherIds}
                    toggleVoucher={toggleVoucher}
                  />
                ))}
              </Stack>
            ))}
            {promotionsData.isFetchingNextPage && (
              <PromoItemLoading justify="center" align="center" />
            )}
          </>
        ) : (
          <EmptyPromotion />
        )}
        <Space h={24} />
      </ScrollArea>
    </Box>
  );
}
